import type { ReactNode } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import type { ReviewBulkRowAction } from './purchasingReviewTableConfig'

export type PurchasingReviewBulkActionBarProps = {
  selectedCount: number
  bulkActions: ReviewBulkRowAction[]
  onClearSelection: () => void
  onApprove: () => void
  onLink: () => void
  onLinkProduct: () => void
  onDefinePack: () => void
  onEdit: () => void
  onExport: () => void
}

const onBarButtonClassName =
  'border-white/30 bg-white/10 text-white hover:bg-white/20 hover:text-white'

const labelRowClassName = 'flex min-h-[1.25rem] items-end justify-center pb-1'

function ActionBarGroup({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      {label ? (
        <div className={`${labelRowClassName} border-b border-white/25`}>
          <p className="text-center text-xs font-medium text-white/80">{label}</p>
        </div>
      ) : (
        <div className={labelRowClassName} aria-hidden />
      )}
      <div className="flex min-h-8 flex-wrap items-center gap-2">{children}</div>
    </div>
  )
}

function GroupSeparator() {
  return <div className="hidden w-px self-stretch bg-white/25 sm:block" aria-hidden />
}

export default function PurchasingReviewBulkActionBar({
  selectedCount,
  bulkActions,
  onClearSelection,
  onApprove,
  onLink,
  onLinkProduct,
  onDefinePack,
  onEdit,
  onExport,
}: PurchasingReviewBulkActionBarProps) {
  if (selectedCount <= 0) return null

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center px-4">
      <div
        className="pointer-events-auto flex flex-wrap items-end gap-3 rounded-xl px-4 py-3 text-white shadow-2xl shadow-gray-900/25"
        style={{ background: 'var(--gradient-primary)' }}
      >
        <ActionBarGroup>
          <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-sm font-bold tabular-nums">
            {selectedCount.toLocaleString('en-US')}
          </span>
          <span className="text-sm text-white/90">selected</span>
        </ActionBarGroup>

        <GroupSeparator />

        <ActionBarGroup label="Edit">
          <Button size="sm" variant="outline" className={onBarButtonClassName} onClick={onEdit}>
            Edit
          </Button>
        </ActionBarGroup>

        {bulkActions.length > 0 ? (
          <>
            <GroupSeparator />
            <ActionBarGroup label="Actions">
              {bulkActions.includes('link') ? (
                <Button
                  size="sm"
                  variant="outline"
                  className={onBarButtonClassName}
                  onClick={onLink}
                >
                  Link
                </Button>
              ) : null}
              {bulkActions.includes('link_product') ? (
                <Button
                  size="sm"
                  variant="outline"
                  className={onBarButtonClassName}
                  onClick={onLinkProduct}
                >
                  Link product…
                </Button>
              ) : null}
              {bulkActions.includes('define_pack') ? (
                <Button
                  size="sm"
                  variant="outline"
                  className={onBarButtonClassName}
                  onClick={onDefinePack}
                >
                  Define pack
                </Button>
              ) : null}
              {bulkActions.includes('approve') ? (
                <Button
                  size="sm"
                  variant="outline"
                  className={onBarButtonClassName}
                  onClick={onApprove}
                >
                  Approve
                </Button>
              ) : null}
            </ActionBarGroup>
          </>
        ) : null}

        <GroupSeparator />

        <ActionBarGroup label="Export">
          <Button size="sm" variant="outline" className={onBarButtonClassName} onClick={onExport}>
            Export CSV
          </Button>
        </ActionBarGroup>

        <ActionBarGroup>
          <Button
            size="sm"
            variant="ghost"
            iconOnly
            aria-label="Clear selection"
            className="text-white/80 hover:bg-white/15 hover:text-white"
            onClick={onClearSelection}
          >
            <XMarkIcon className="h-4 w-4" />
          </Button>
        </ActionBarGroup>
      </div>
    </div>
  )
}
